import { ThemeProvider } from "@mui/system";
import { useEffect, useLayoutEffect, useState } from "react";
import { Provider, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { theme } from "./Customization/Theme";
import useVersionCheck from "./Hooks/useVersionCheck";
import store, { useAppSelector } from "./Store";
import { useAuthStore } from "./Store/Slices/AuthSlice";
import ThemedApp from "./ThemedApp";
import { validateToken } from "./Utils/Utils";
import {
  AnimationStyles,
  ButtonContainer,
  CancelButton,
  CheckboxContainer,
  CheckboxLabel,
  ContentWrapper,
  HighlightedText,
  IconCircle,
  InfoBox,
  Overlay,
  StyledCheckbox,
  TextContainer,
  TitleText,
  UpdateButton,
  VersionPopupContainer,
  VersionText,
} from "./Utils/VersionPopupStyles";
import { localStoragePersister, queryClient } from "./Utils/tanstack-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { defaultShouldDehydrateQuery } from "@tanstack/react-query";
import useProduktlyUser from "./Hooks/useProduktlyUser";
import AppModel from "./Hooks/useModel/AppModel";
import { AppConfig } from "./Config/app-config";
import * as Sentry from "@sentry/react";
import styled from "styled-components";
import { coreAxios } from "./Utils/Axios";
import AppButton from "./AppComponents/AppButton";
import { UserNetworkService } from "./Store/Slices/Settings/LiveChat/User/UserNetworkService";
import { useCurrentBot } from "./Store/Slices/DashboardSlices";
import { ApidomainDetails } from "./Store/Slices/SubAccount/subaccount.selectors";

validateToken();


if (window.location.origin === "https://app.wabo.ai/") {
  Sentry.init({
    dsn: "https://c38cc9c1273082c611f3ec8baf54831b@o4507446172188672.ingest.us.sentry.io/4507447376019456",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    environment: process.env.REACT_APP_SENTRY_ENV,
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ["localhost"],
    tracePropagationTargets: [process.env.REACT_APP_SENTRY_DOMAIN as string],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

interface AnimatedUpdateNotificationProps {
  isUpdateAvailable: any;
  currentVersion: any;
  latestVersion: any;
  updateApp: () => any;
  dismissPopup: any;
}

const AnimatedUpdateNotification: React.FC<AnimatedUpdateNotificationProps> = ({
  isUpdateAvailable,
  currentVersion,
  latestVersion,
  updateApp,
  dismissPopup,
}) => {
  const [animationState, setAnimationState] = useState("hidden");
  const [dismissed, setDismissed] = useState(false);
  const { userConfig } = useAppSelector(useAuthStore);
  useLayoutEffect(() => {
    const data = AppConfig.getLoggedInData();
    const workspaceId = data?.data?.user?.workspace_id;
    if (workspaceId) {
      localStorage.setItem("ws_id", workspaceId);
    } else {
      console.log("Workspace ID not found in logged in data");
    }
  }, []); 
  useEffect(() => {
    if (isUpdateAvailable) {
      setAnimationState("entering");
      const timer = setTimeout(() => {
        setAnimationState("stable");
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setAnimationState("hidden");
    }
  }, [isUpdateAvailable]);

  // Hiding the version update popup when these conditions occurs......
  if (animationState === "hidden") return null;

  return (
    <>
      <Overlay animationState={animationState} />
      <VersionPopupContainer animationState={animationState}>
        <ContentWrapper>
          <IconCircle>🎉</IconCircle>

          <TextContainer>
            <TitleText>A new version is available!</TitleText>
            <VersionText>
              Current: {currentVersion} → Latest: {latestVersion}
            </VersionText>
          </TextContainer>
        </ContentWrapper>

        <ButtonContainer>
          <CancelButton onClick={dismissPopup}>Cancel</CancelButton>
          <UpdateButton onClick={updateApp}>Update Now</UpdateButton>
        </ButtonContainer>
      </VersionPopupContainer>

      <AnimationStyles />
    </>
  );
};

const AppDisabledFBM = (): JSX.Element => {
  const { userConfig } = useAppSelector(useAuthStore);

  // Use useEffect to update state when userConfig changes
  const [showModal, setShowModal] = useState(
    userConfig?.payload?.whatsapp_enabled
  );

  useEffect(() => {
    // Show modal if userConfig exists and WhatsApp is disabled
    if (
      userConfig &&
      userConfig.payload &&
      userConfig.payload.whatsapp_enabled === false
    ) {
      setShowModal(true);
    }
  }, [userConfig]);

  const dismissPopup = () => {
    setShowModal(false);
  };
  console.log("🚀 ~ AppDisabledFBM ~ userConfig:", userConfig)

  const animationState = "stable"; // Changed from "hidden" to "stable" to show the modal

  return (
    <AppModel isShowing={showModal} onClose={dismissPopup}>
      <Overlay animationState={animationState} />
      <VersionPopupContainer
        animationState={animationState}
        height="500px"
        width="500px"
      >
        <ContentWrapper>
          <IconCircle>🚫</IconCircle>
          <TextContainer>
            <TitleText>
              Your WhatsApp Business Number Has Been Disabled by Meta
            </TitleText>
            <VersionText>
              Unfortunately, your WhatsApp number appears to be disabled or
              restricted by Meta, which means you're currently unable to send or
              receive messages. But don’t worry — you can submit a review to
              request reinstatement directly from your WhatsApp Business
              Manager.
            </VersionText>
          </TextContainer>
          <a
            href="https://help.wabo.ai/getting-started/how-to-re-enable-a-phone-number-disabled-by-meta-on-whatsapp-business"
            target="_blank"
            rel="noopener noreferrer"
          >
            Steps to Re-enable a Disabled WhatsApp Number
          </a>
        </ContentWrapper>

        <div
          style={{
            width: "100%",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "flex-end",
            marginTop: "1rem",
          }}
        >
          <AppButton
            variant="outline"
            buttonStyle={{ width: "100px" }}
            onClick={() => {
              dismissPopup();
            }}
          >
            Close
          </AppButton>

          <AppButton
            variant="primary"
            buttonStyle={{ width: "200px" }}
            onClick={() => {
              window.open(
                "https://business.facebook.com/settings/whatsapp/review",
                "_blank"
              );
              dismissPopup();
            }}
          >
            Submit Review to META
          </AppButton>
        </div>
      </VersionPopupContainer>

      <AnimationStyles />
    </AppModel>
  );
};

const WhatsappPricingPopup = (): JSX.Element | null => {
  const [showModal, setShowModal] = useState(false);
  const [hasShownThisSession, setHasShownThisSession] = useState(false);
  const { userConfig } = useAppSelector(useAuthStore);
  const location = useLocation();
  // console.log("🚀 ~ MarketingPricingPopup ~ userConfig:", userConfig)
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const currentBot = useSelector(useCurrentBot);
  // console.log("🚀 ~ MarketingPricingPopup ~ currentBot:", currentBot)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userData: any = AppConfig.getUserData("loggedInUserData")?.data?.user;
  // console.log("🚀 ~ MarketingPricingPopup ~ userData:", userData)
  const domainApiSuccess: any = useAppSelector(ApidomainDetails);
  const disable = userConfig?.payload?.sub_account_type === "user" && domainApiSuccess === true;
  
  useEffect(() => {
    if (disable) {
      setShowModal(false);
      return;
    }

    // Don't show popup on pricing page or OTP verification page
    if (location.pathname === '/whatsapp/pricing' || location.pathname === '/verify-otp') {
      setShowModal(false);
      return;
    }
    
    // Sync localStorage with backend status on first load
    if (userConfig?.payload?.price_acknowledgement_status) {
      localStorage.setItem('whatsapp_pricing_acknowledged', 'true');
    }
    
    // Check if popup was already dismissed in localStorage
    const dismissedInLocalStorage = localStorage.getItem('whatsapp_pricing_acknowledged') === 'true';

    // Only show if not dismissed permanently AND not shown in this session yet
    if (!dismissedInLocalStorage && !hasShownThisSession) {
      setShowModal(true);
      setHasShownThisSession(true);
    }
  }, [disable, userConfig, location.pathname, hasShownThisSession]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDontShowAgain(e.target.checked);
  };

  const handleClose = async () => {
    setIsSubmitting(true);
    
    try {
      if (dontShowAgain) {
        const payload = {
          "user_id": userData?.id,
          "user_acknowledged": true
        }
        console.log("🚀 ~ handleClose ~ payload:", payload)
        const HEADERS = {
          appid: JSON.stringify({
            botid: currentBot?.bot_id,
          }),
        };
        
        await UserNetworkService.acknowledgeWhatsappPricingStatus({ payload, HEADERS })
          .then((response: any) => {

            if (response?.status === 200) {
              console.log("🚀 ~ handleClose ~ response:", response)
              // Set localStorage to true only after successful API call
              localStorage.setItem('whatsapp_pricing_acknowledged', 'true');
            }
          })
          .catch((err: any) => {
            console.log("🚀 ~ handleClose ~ err:", err)
          })
      }
      
      // Always close the modal after handling the API call
      setShowModal(false);
    } catch (error) {
      console.error("Error updating marketing pricing notification preference:", error);
      setShowModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const animationState = showModal ? "stable" : "hidden";

  if (disable || !showModal) return null;

  return (
    <AppModel isShowing={showModal} onClose={handleClose}>
      <Overlay animationState={animationState} />
      <VersionPopupContainer
        animationState={animationState}
        height="535px"
        width="600px"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <ContentWrapper>
          <IconCircle style={{ backgroundColor: "#FFF3E0" }}>📢</IconCircle>

          <TextContainer>
            <TitleText style={{ fontSize: "22px", marginBottom: "12px" }}>
              WhatsApp Pricing Update by Meta
            </TitleText>

            <VersionText style={{ fontSize: "16px", lineHeight: "1.6", textAlign: "left" }}>
              Meta has announced a revision in the pricing for WhatsApp messages.
            </VersionText>

            <VersionText style={{ fontSize: "16px", lineHeight: "1.6", textAlign: "left", marginTop: "12px" }}>
              This change will impact the cost of sending WhatsApp message templates
              through the platform.{" "}
              <a href="/whatsapp/pricing" style={{ color: "#007bff", textDecoration: "none" }}>View Pricing</a>
            </VersionText>

            {/* <VersionText style={{ fontSize: "16px", lineHeight: "1.6", textAlign: "left", marginTop: "12px" }}>
              <a href="/whatsapp/pricing" style={{ color: "#007bff", textDecoration: "none" }}>View Pricing</a>
            </VersionText> */}

            <InfoBox>
              <VersionText style={{ fontSize: "15px", margin: 0 }}>
                The updated pricing will take effect from <strong>January 1, 2026</strong>, and the revised rates will be reflected in your billing from that date onward.
              </VersionText>
            </InfoBox>
          </TextContainer>

          <CheckboxContainer onClick={() => setDontShowAgain(!dontShowAgain)}>
            <StyledCheckbox
              type="checkbox"
              id="dontShowAgain"
              checked={dontShowAgain}
              onChange={handleCheckboxChange}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
            />
            <CheckboxLabel htmlFor="dontShowAgain">
              I understood, don't show me this message again.
            </CheckboxLabel>
          </CheckboxContainer>
        </ContentWrapper>

        <div
          style={{
            width: "100%",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "20px",
          }}
        >
          <AppButton
            variant="outline"
            buttonStyle={{ width: "200px" }}
            onClick={handleClose}
          >
            {isSubmitting ? "Closing..." : "Close"}
          </AppButton>
        </div>
      </VersionPopupContainer>

      <AnimationStyles />
    </AppModel>
  );
};

const AppContent = () => {
  const { userConfig } = useAppSelector(useAuthStore);
  const isAuthenticated = userConfig !== null;
  const isCustomPlan = userConfig?.payload?.is_custom;
  const isWhatsAppEnabled = userConfig?.payload?.whatsapp_enabled;
  const {
    isUpdateAvailable,
    updateApp,
    dismissPopup,
    currentVersion,
    latestVersion,
  }: any = useVersionCheck(isAuthenticated);

  // Use the Produktly user identification hook
  useProduktlyUser();

  return (
    <ThemeProvider theme={theme}>
      {(isAuthenticated && userConfig?.payload?.price_acknowledgement_status) && (
        <>
          <AnimatedUpdateNotification
            isUpdateAvailable={isUpdateAvailable}
            currentVersion={currentVersion}
            latestVersion={latestVersion}
            updateApp={updateApp}
            dismissPopup={dismissPopup}
          />
        </>
      )}
      {(isAuthenticated && !isCustomPlan) && <WhatsappPricingPopup />}
      {/* {isAuthenticated && !isWhatsAppEnabled && <AppDisabledFBM />} */}
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: localStoragePersister,
        dehydrateOptions: {
          shouldDehydrateQuery: (query: any) =>
            defaultShouldDehydrateQuery(query) && query.meta?.persist === true,
        },
        maxAge: 1000 * 60 * 10, // 10 minutes
      }}
    >
      <Provider store={store}>
        <ThemedApp />
        <AppContent />
      </Provider>
    </PersistQueryClientProvider>
  );
};

export default App;
